import { useEffect, useState, type FormEvent } from "react";
import { Loader2, X } from "lucide-react";
import { request } from "../lib/api";
import type { AudienceRule, AudienceTargets } from "../types";
import {
  ErrorState,
  InlineNotice,
  errorMessage,
  inputClass,
  labelClass,
  primaryButtonClass,
  secondaryButtonClass,
} from "./ui";

type Kind = "group" | "district" | "institute" | "grade" | "subject";

const KIND_LABELS: Record<Kind, string> = {
  group: "Study group",
  district: "District",
  institute: "School or centre",
  grade: "Grade",
  subject: "Subject",
};

interface AudienceModalProps {
  resourceType: "module" | "content_item";
  resourceID: string;
  /** What the rules govern, as named on screen. */
  title: string;
  onClose: () => void;
}

// Who sees one module or content item. With no rule everyone does; each rule
// narrows that. The group's own label (treatment or control) changes nothing:
// only a rule here does.
export function AudienceModal({ resourceType, resourceID, title, onClose }: AudienceModalProps) {
  const [rules, setRules] = useState<AudienceRule[] | null>(null);
  const [targets, setTargets] = useState<AudienceTargets | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [kind, setKind] = useState<Kind>("group");
  const [value, setValue] = useState("");
  const [mode, setMode] = useState<"only" | "hide">("only");

  async function loadRules() {
    try {
      const data = await request<{ rules: AudienceRule[] }>(
        `/api/admin/audience-rules?resource_type=${resourceType}&resource_id=${resourceID}`
      );
      setRules(data.rules);
    } catch (cause) {
      setError(errorMessage(cause));
    }
  }

  useEffect(() => {
    void loadRules();
    request<AudienceTargets>("/api/admin/audience-targets")
      .then(setTargets)
      .catch((cause) => setError(errorMessage(cause)));
  }, [resourceType, resourceID]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const options: { value: string; label: string }[] = !targets
    ? []
    : kind === "group"
      ? targets.groups.map((group) => ({
          value: group.id,
          label: `${group.name} (${group.cohort})`,
        }))
      : kind === "district"
        ? targets.districts.map((district) => ({ value: district.id, label: district.name }))
        : kind === "institute"
          ? targets.institutes.map((school) => ({
              value: school.id,
              label: school.is_center ? `${school.name} (centre)` : school.name,
            }))
          : (kind === "grade" ? targets.grades : targets.subjects).map((text) => ({
              value: text,
              label: text,
            }));

  async function add(event: FormEvent) {
    event.preventDefault();
    if (!value) return;
    setBusy(true);
    setError("");
    const byID = kind === "group" || kind === "district" || kind === "institute";
    try {
      await request("/api/admin/audience-rules", {
        method: "POST",
        body: JSON.stringify({
          resource_type: resourceType,
          resource_id: resourceID,
          target_type: kind,
          target_id: byID ? value : null,
          target_value: byID ? null : value,
          include: mode === "only",
        }),
      });
      setValue("");
      await loadRules();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  async function remove(rule: AudienceRule) {
    setBusy(true);
    setError("");
    try {
      await request(`/api/admin/audience-rules/${rule.id}`, { method: "DELETE" });
      await loadRules();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  const onlyCount = rules?.filter((rule) => rule.include).length ?? 0;
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Who sees ${title}`}
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <div className="grid max-h-[90vh] w-full max-w-lg gap-4 overflow-y-auto rounded-lg border border-border bg-surface p-5 shadow-lg">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-content-primary">Who sees this?</h2>
            <p className="truncate text-sm text-content-secondary">{title}</p>
          </div>
          <button
            type="button"
            className="text-content-secondary hover:text-content-primary"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <p className="text-sm leading-6 text-content-secondary">
          Everyone who reaches this sees it, unless you add a rule below. A group&apos;s label
          (treatment or control) changes nothing by itself; these rules do.
        </p>

        {!rules ? (
          error ? (
            <ErrorState message={error} />
          ) : (
            <p className="text-sm text-content-secondary">Loading…</p>
          )
        ) : rules.length === 0 ? (
          <p className="rounded-md bg-surface-muted px-3 py-2 text-sm text-content-primary">
            No rules: shown to everyone.
          </p>
        ) : (
          <ul className="grid gap-2">
            {rules.map((rule) => (
              <li
                key={rule.id}
                className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2 text-sm"
              >
                <span className="min-w-0">
                  <strong className="font-semibold">
                    {rule.include ? "Only shown to" : "Hidden from"}
                  </strong>{" "}
                  {rule.label}
                  <span className="block text-xs text-content-secondary">
                    {KIND_LABELS[
                      (rule.target_type === "center" ? "institute" : rule.target_type) as Kind
                    ] ?? rule.target_type}
                  </span>
                </span>
                <button
                  type="button"
                  className="shrink-0 text-xs font-semibold text-danger hover:underline disabled:opacity-40"
                  disabled={busy}
                  onClick={() => void remove(rule)}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}

        {onlyCount > 1 && (
          <p
            role="alert"
            className="rounded-md bg-warning-bg px-3 py-2 text-xs leading-5 text-warning-text"
          >
            There is more than one &ldquo;Only shown to&rdquo; rule. A teacher has to match all of
            them, which is usually nobody. Remove one, or use &ldquo;Hide from&rdquo; instead.
          </p>
        )}

        <form className="grid gap-3 border-t border-border pt-4" onSubmit={add}>
          <h3 className="text-sm font-semibold text-content-primary">Add a rule</h3>
          <label className={labelClass}>
            What to do
            <select
              className={inputClass}
              value={mode}
              onChange={(event) => setMode(event.target.value as "only" | "hide")}
            >
              <option value="only">Only show to…</option>
              <option value="hide">Hide from…</option>
            </select>
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className={labelClass}>
              Who
              <select
                className={inputClass}
                value={kind}
                onChange={(event) => {
                  setKind(event.target.value as Kind);
                  setValue("");
                }}
              >
                {(Object.keys(KIND_LABELS) as Kind[]).map((key) => (
                  <option key={key} value={key}>
                    {KIND_LABELS[key]}
                  </option>
                ))}
              </select>
            </label>
            <label className={labelClass}>
              Which
              <select
                className={inputClass}
                value={value}
                onChange={(event) => setValue(event.target.value)}
                disabled={!targets}
              >
                <option value="">{options.length ? "Choose…" : "None available"}</option>
                {options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {error && rules && <InlineNotice tone="error" text={error} />}
          <div className="flex justify-end gap-2">
            <button type="button" className={secondaryButtonClass} onClick={onClose}>
              Done
            </button>
            <button className={primaryButtonClass} disabled={busy || !value}>
              {busy && <Loader2 size={16} className="animate-spin" />}
              Add rule
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
